import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import {
  usePaneLocation,
  usePaneParams,
  usePaneRouterContextSafe,
} from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import { cn } from "@/utils";
import {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { ExternalLink, FileIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LoadingOverlay } from "../file-record-detail/LoadingOverlay";
import {
  getGoogleDrivePreviewUrl,
  upscaleDriveIcon,
} from "./googleDrivePreview";

export function GoogleDriveFileDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const mainLocation = useLocation();
  const { fileId } = usePaneParams(ROUTES.GOOGLE_DRIVE.FILE_DETAIL);

  // Entity is passed via route state (same pattern as GitHub/Notion/Email panes)
  const entity = paneLocation?.state?.entity ?? mainLocation.state?.entity;
  const data = (entity?.data ?? {}) as Record<string, unknown>;

  const id = (data.id as string | undefined) ?? fileId ?? "";
  const title = data.title as string | undefined;
  const mimeType = data.mime_type as string | undefined;
  // Generic entities (base: "generic") carry url + platform_name but not the
  // typed Drive fields — fall back accordingly.
  const platformName = data.platform_name as string | undefined;
  const webLink =
    (data.web_link as string | undefined) ?? (data.url as string | undefined);
  const iconLink = data.icon_link as string | undefined;

  const [iframeLoaded, setIframeLoaded] = useState(false);

  const previewUrl = getGoogleDrivePreviewUrl(id, mimeType, platformName);
  const crispIcon = upscaleDriveIcon(iconLink);

  // The pane stays mounted when navigating between Drive files — reset the
  // loading overlay so the new iframe doesn't mount behind a hidden overlay.
  useEffect(() => {
    setIframeLoaded(false);
  }, [previewUrl]);

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const handleOpenExternal = useCallback(() => {
    if (webLink) {
      window.open(webLink, "_blank", "noopener,noreferrer");
    }
  }, [webLink]);

  const renderBody = () => {
    if (previewUrl) {
      // Mirrors PdfContent / OfficeContent: iframe fades in over a neutral
      // backdrop, simple spinner overlay until then. The 300ms settle delay
      // lets Google's viewer paint before the reveal.
      return (
        <div className="relative h-full w-full bg-muted/20">
          <iframe
            key={previewUrl}
            src={previewUrl}
            title={title ?? "Google Drive preview"}
            className={cn(
              "w-full h-full border-0 transition-opacity duration-300",
              iframeLoaded ? "opacity-100" : "opacity-0",
            )}
            allow="autoplay"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            onLoad={() => setTimeout(() => setIframeLoaded(true), 300)}
          />
          {!iframeLoaded && <LoadingOverlay />}
        </div>
      );
    }

    // Fallback for folders, shortcuts, and non-previewable types
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
        {crispIcon ? (
          <img src={crispIcon} alt="" className="h-16 w-16 object-contain" />
        ) : (
          <FileIcon className="h-16 w-16 text-muted-foreground" />
        )}
        <p className="text-sm font-medium text-foreground leading-snug">
          {title ?? "Untitled"}
        </p>
        {webLink && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleOpenExternal}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in Google Drive
          </Button>
        )}
      </div>
    );
  };

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />

          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            {iconLink ? (
              <img
                src={iconLink}
                alt=""
                className="h-4 w-4 shrink-0 object-contain"
              />
            ) : (
              <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {title ?? "Untitled"}
            </span>
          </div>

          <TooltipProvider>
            {entity && (
              <EntityPaneHeaderObjectActions entity={entity} name={title} />
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0" noScroll={!!previewUrl}>
        {renderBody()}
      </PaneLayoutContent>
    </PaneLayout>
  );
}
